import { tCalendar, getCalendarLang } from '../../lib/calendar-i18n';

/**
 * 알림 「사용자 지정」 · 「날짜·시간 지정」 (2026-10-16 · 사용자가 고른 «방법 1»).
 *
 * 서버는 알림을 «일정 시작 몇 분 전»(minutesBefore, 0 ~ 40320 = 4주) 하나로만 저장한다.
 * 그래서 화면이 고른 값을 그 분(分)으로 바꿔 보낸다 — 서버·DB·앱은 그대로다.
 * ⚠ 날짜·시간으로 골라도 저장되는 것은 «몇 분 전»이다 ⇒ 나중에 일정 시각을 옮기면 알림도 따라 움직인다.
 * ⚠ 반복 일정에서는 «그 날짜·시각» 이 성립하지 않는다(회차마다 그만큼 전이 된다) — 화면이 그 선택지를 숨긴다.
 */

/** 서버 상한(reminders-helpers.ts createReminderBodySchema maximum) — 4주. */
const REMINDER_MAX_MINUTES = 40320;

export type ReminderUnit = 'min' | 'hour' | 'day' | 'week';
const UNIT_MINUTES: Record<ReminderUnit, number> = { min: 1, hour: 60, day: 1440, week: 10080 };

export type ReminderPick =
  | { ok: true; minutes: number }
  | { ok: false; reason: 'range' | 'after_start' | 'past' | 'invalid' };

/** 「사용자 지정」 — 숫자 × 단위. 1분 이상 4주 이하의 정수만. */
export function minutesFromCustom(amount: string | number, unit: ReminderUnit): ReminderPick {
  const n = typeof amount === 'number' ? amount : Number(String(amount).trim());
  if (!Number.isInteger(n) || n <= 0) return { ok: false, reason: 'invalid' };
  const m = n * UNIT_MINUTES[unit];
  if (m > REMINDER_MAX_MINUTES) return { ok: false, reason: 'range' };
  return { ok: true, minutes: m };
}

/** 「날짜·시간 지정」 — 그 시각이 일정 시작보다 몇 분 앞인가. 분 단위로 내린다. */
export function minutesFromAt(at: Date, eventStart: Date, now: Date): ReminderPick {
  const a = at.getTime();
  const s = eventStart.getTime();
  if (Number.isNaN(a) || Number.isNaN(s)) return { ok: false, reason: 'invalid' };
  if (a > s) return { ok: false, reason: 'after_start' };
  if (a <= now.getTime()) return { ok: false, reason: 'past' };
  const m = Math.floor((s - a) / 60000);
  if (m > REMINDER_MAX_MINUTES) return { ok: false, reason: 'range' };
  return { ok: true, minutes: m };
}

/** 알림 칩 글자 — 「10분 전」 · 「1일 전」, 섞이면 「1일 3시간 30분 전」. */
export function reminderLabel(min: number): string {
  if (min === 0) return tCalendar('calendar.reminder.at_time');
  if (min % 1440 === 0) return tCalendar('calendar.reminder.day_before', { n: min / 1440 });
  if (min % 60 === 0 && min < 1440) return tCalendar('calendar.reminder.hour_before', { n: min / 60 });
  if (min < 60) return tCalendar('calendar.reminder.min_before', { n: min });
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  const m = min % 60;
  const parts = [
    d ? tCalendar('calendar.reminder.part_day', { n: d }) : '',
    h ? tCalendar('calendar.reminder.part_hour', { n: h }) : '',
    m ? tCalendar('calendar.reminder.part_min', { n: m }) : '',
  ].filter(Boolean).join(getCalendarLang() === 'ja' ? '' : ' ');   // 일본어는 붙여 쓴다(1日3時間)
  return tCalendar('calendar.reminder.parts_before', { parts });
}

/** 날짜·시간 피커의 값 꼴 — time-label.ts 하나로(2026-10-19 정리). */
export { toPickerValue } from './time-label';
